/**
 * @fileoverview Utility for computing plot scope guidance based on projected word count.
 *
 * Provides agents with per-section paragraph targets for the writtenPlot,
 * based on the 7 structural phases used across all agents:
 *   Initial State, Inciting Incident, Break Into Two, Midpoint,
 *   Break Into Three, Climax, Final State.
 *
 * Short-form tiers (by word count):
 *   <3,000 → 1 paragraph per section
 *   3,000–6,000 → 2 paragraphs per section
 *   >6,000 → 3 paragraphs per section
 *
 * Long-form tiers (by word count):
 *   <25,000 → 3 paragraphs per section
 *   25,000–50,000 → 4 paragraphs per section
 *   50,000–75,000 → 5 paragraphs per section
 *   75,000–100,000 → 6 paragraphs per section
 *   100,000–150,000 → 7 paragraphs per section
 *   150,000–200,000 → 8 paragraphs per section
 *   200,000–500,000 → 9 paragraphs per section
 *   500,000+ → 10 paragraphs per section
 */

const STRUCTURAL_PHASES = [
  'Initial State',
  'Inciting Incident',
  'Break Into Two',
  'Midpoint',
  'Break Into Three',
  'Climax',
  'Final State',
] as const;

const NUM_SECTIONS = STRUCTURAL_PHASES.length; // 7

export interface PlotScopeGuidance {
  /** Total paragraphs across all 7 structural phase sections. */
  writtenPlotParagraphs: number;
  /** Paragraphs per structural phase section (7 sections). */
  paragraphsPerSection: number;
  /** The rendered guidance string to inject into agent prompts. */
  userMessageGuidance: string;
}

/**
 * Computes scope guidance for plot generation based on projected word count.
 *
 * Both short-form and long-form use the same 7 structural phase sections.
 * The difference is in per-section paragraph density (see tiers in file header).
 *
 * @param projectedWordCount - The projected total word count for the story
 * @param isShortForm - Whether this is short-form fiction (Flash Fiction / Short Story)
 * @returns Scope guidance for the agent
 */
export function computePlotScopeGuidance(
  projectedWordCount: number | undefined,
  isShortForm: boolean
): PlotScopeGuidance {
  const wordCount = projectedWordCount || (isShortForm ? 5000 : 50000);

  const paragraphsPerSection = isShortForm
    ? computeShortFormParagraphsPerSection(wordCount)
    : computeLongFormParagraphsPerSection(wordCount);

  const writtenPlotParagraphs = paragraphsPerSection * NUM_SECTIONS;

  const userMessageGuidance = generateGuidanceMessage(
    wordCount,
    paragraphsPerSection,
    writtenPlotParagraphs,
    isShortForm
  );

  return {
    writtenPlotParagraphs,
    paragraphsPerSection,
    userMessageGuidance,
  };
}

// =============================================================================
// PARAGRAPH-PER-SECTION TIERS
// =============================================================================

function computeShortFormParagraphsPerSection(wordCount: number): number {
  if (wordCount < 3000) return 1;
  if (wordCount <= 6000) return 2;
  return 3;
}

function computeLongFormParagraphsPerSection(wordCount: number): number {
  if (wordCount < 25000) return 3;
  if (wordCount < 50000) return 4;
  if (wordCount < 75000) return 5;
  if (wordCount < 100000) return 6;
  if (wordCount < 150000) return 7;
  if (wordCount < 200000) return 8;
  if (wordCount < 500000) return 9;
  return 10;
}

// =============================================================================
// GUIDANCE MESSAGE GENERATION
// =============================================================================

/**
 * Generates a formatted guidance message to inject into agent prompts.
 */
function generateGuidanceMessage(
  wordCount: number,
  paragraphsPerSection: number,
  totalParagraphs: number,
  isShortForm: boolean
): string {
  const storyLength = getStoryLengthCategory(wordCount);

  const sectionList = STRUCTURAL_PHASES
    .map(phase => `- ## ${phase}`)
    .join('\n');

  // For very short forms (1 paragraph per section), the uneven-distribution
  // framing doesn't really apply — every phase needs to exist and there is
  // little room to redistribute.
  if (isShortForm && paragraphsPerSection === 1) {
    return `## SCOPE GUIDANCE
The projected word count is ~${wordCount.toLocaleString()} words (${storyLength}). At this length, a single paragraph per structural phase is usually enough — roughly ~${totalParagraphs} paragraphs total across the 7 headings:

${sectionList}

Each phase should be present, but keep things compact. A paragraph may capture a single beat or a brief arc; whatever the story needs to move forward.`;
  }

  return `## SCOPE GUIDANCE
The projected word count is ~${wordCount.toLocaleString()} words (${storyLength}). For a story of this length, the writtenPlot tends to settle somewhere around ~${totalParagraphs} paragraphs, distributed across the 7 structural phases:

${sectionList}

Treat ~${totalParagraphs} as a rough budget rather than a quota. The distribution across phases is rarely even — phases carrying more narrative weight (often Midpoint and Climax, but it depends on the story) will naturally pull more paragraphs, while quieter phases stay leaner. Let the shape of the story decide where the density lands; aim for a total roughly in that neighborhood and don't worry about hitting it precisely.

Paragraphs don't need to map one-to-one to discrete beats. Some can anchor a single turning point; others can trace a longer movement, an arc of escalation, or a stretch of consequence. Vary the granularity in service of the story rather than chasing a uniform paragraph shape.`;
}

/**
 * Returns a human-readable category for the story length.
 */
function getStoryLengthCategory(wordCount: number): string {
  if (wordCount < 1000) return 'Flash Fiction';
  if (wordCount < 7500) return 'Short Story';
  if (wordCount < 20000) return 'Novelette';
  if (wordCount < 50000) return 'Novella';
  if (wordCount < 110000) return 'Novel';
  return 'Epic';
}
